import {CleanHtml} from '@frontapp/editor-html';
import _, {unescape, uniq} from 'lodash';

import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  getRegexpMatches,
  isDigit,
  makeVariableFriendly,
} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {
  answerVariablePluginDataAttribute,
  answerVariablePluginTopicIdAttribute,
} from '../../components/inboxes/composer/composerConstants';
import {AnswerModel} from '../../models/api/answerModel';
import {ApplicationModel, ApplicationPropertyModel} from '../../models/api/applicationModel';
import {ApplicationSettingsModel} from '../../models/api/applicationSettingsModel';
import {TopicModel} from '../../models/api/topicModel';
import {variableRegexp} from '../../services/variableInterpolation/variableInterpolator';
import {ExpressionsContext} from '../../settings/editors/expressions/expressionsContext';
import {InferredTopicType} from '../../settings/editors/expressions/infer/expressionResultModel';

export const appPrefix = 'app';
export const defaultSchema = 'default';

const appVariableNameRegex =
  /^app\.(?<appName>[^.]+)\.(?<objectName>[^.]+)\.(?:custom.)?(?<propertyName>[^.]+)$/g;
const topicIdAttributeRegex = new RegExp(
  `${answerVariablePluginTopicIdAttribute}="[^"]+:(?<topicId>[^"]+)"`,
  'g',
);
const variableNameAttributeRegex = new RegExp(
  `${answerVariablePluginDataAttribute}="(?<variableName>[^"]+)"`,
  'g',
);

export enum ApplicationObjectStandardPropertiesEnum {
  NAME = 'name',
  DESCRIPTION = 'description',
}

export interface MappedObject {
  name: string;
  uid: string;
  properties: ReadonlyArray<ApplicationPropertyModel>;
}

interface Property {
  name: string;
  id: string | number;
}

export interface ComposerVariablesState {
  objectUid: string;
  topicId: number | undefined;
}

export interface ApplicationObjectSlugs {
  sluggifiedAppName: string;
  sluggifiedObjectName: string;
}

/**
 * Computes whether html contains variables from multiple app objects
 *
 * @param html The string of html
 * @returns A boolean of whether the string contains variables from multiple app objects
 */
export function findHasMultipleAppObjectsInHtml(html: CleanHtml): boolean {
  const appObjectSlugs = findApplicationObjectSlugsInHtml(html);
  const uniqueAppObjects = uniq(
    appObjectSlugs.map((name) => `${name.sluggifiedAppName}${name.sluggifiedObjectName}`),
  );
  return uniqueAppObjects.length > 1;
}

/**
 * Computes whether html contains variables from at least one app object
 *
 * @param html The string of html
 * @returns A boolean of whether the string contains variables from at least one app object
 */
export function findHasAppObjectInHtml(html: CleanHtml): boolean {
  return findApplicationObjectSlugsInHtml(html).length > 0;
}

export function findApplicationObjectSlugsInHtml(
  html: CleanHtml | string,
): ReadonlyArray<ApplicationObjectSlugs> {
  const variableMatches = getRegexpMatches(variableRegexp, html);
  return _(variableMatches)
    .map((variableMatch) => {
      const unescapedVariableName = unescape(variableMatch[1]);
      return findAppObjectSlugsFromVariableName(unescapedVariableName);
    })
    .compact()
    .value();
}

export function findApplicationObjectUidForAnswer(
  answer: AnswerModel,
  applications: ReadonlyArray<ApplicationModel>,
): string | undefined {
  const applicationObjectSlugs = findApplicationObjectSlugsInHtml(answer.body)[0];
  const applicationObjectUid = findApplicationObjectUidFromSlugs(applicationObjectSlugs, applications);

  if (!applicationObjectUid) {
    return undefined;
  }

  return applicationObjectUid;
}

/**
 * Find the app object from the first instance of an app variable in message body.
 *
 * @param html the message body to parse
 * @returns the app object or undefined
 */
export function findFirstAppObjectSlugsInInterpolatedMessage(
  html: CleanHtml,
): ApplicationObjectSlugs | undefined {
  const variableMatches = getRegexpMatches(variableNameAttributeRegex, html);

  return variableMatches
    .map((variableMatch) => {
      const unescapedVariableName = unescape(variableMatch.groups?.variableName);
      const appObjectSlugs = findAppObjectSlugsFromVariableName(unescapedVariableName);
      if (!appObjectSlugs) {
        return undefined;
      }
      return appObjectSlugs;
    })
    .filter(isDefined)[0];
}

/**
 * Find application object names from the variable name.
 *
 * @param variableName The variable name
 * @returns sluggified application object names derived from the variable name or undefined
 */
function findAppObjectSlugsFromVariableName(variableName: string): ApplicationObjectSlugs | undefined {
  const appVariableMatches = getRegexpMatches(appVariableNameRegex, variableName);
  if (appVariableMatches.length === 0) {
    return undefined;
  }

  const appVariableMatch = appVariableMatches[0];

  if (
    !appVariableMatch.groups?.appName ||
    !appVariableMatch.groups?.objectName ||
    !appVariableMatch.groups?.propertyName
  ) {
    return undefined;
  }

  return {
    sluggifiedAppName: unescape(appVariableMatch.groups.appName),
    sluggifiedObjectName: unescape(appVariableMatch.groups.objectName),
  };
}

export function findApplicationObjectUidFromSlugs(
  applicationObjectSlugs: ApplicationObjectSlugs | undefined,
  applications: ReadonlyArray<ApplicationModel>,
): string | undefined {
  if (!applicationObjectSlugs) {
    return undefined;
  }
  return _(applications)
    .filter((app) => makeVariableFriendly(app.name) === applicationObjectSlugs?.sluggifiedAppName)
    .flatMap((app) => app.objects)
    .compact()
    .find((object) => makeVariableFriendly(object.name) === applicationObjectSlugs?.sluggifiedObjectName)?.id;
}

export function isTopicVariableName(variableName: string): boolean {
  return Boolean(findAppObjectSlugsFromVariableName(variableName));
}

/**
 * Maps the property to the variable name
 *
 * @param app The installed application model
 * @param mappedObject The mapped application object
 * @param property The property object
 * @param isCustom Whether the property is a custom field
 * @returns The variable name of the application property
 */
export function mapPropertyToVariableName(
  app: ApplicationModel,
  mappedObject: MappedObject,
  property: Property,
  isCustom: boolean,
) {
  return `${appPrefix}.${computeUserFriendlyIdentifier(app, mappedObject, property, isCustom)}`;
}

/**
 * Computes the property's user friendly name
 *
 * @param app The installed application model
 * @param mappedObject The mapped application object
 * @param property The property object
 * @param isCustom Whether the property is a custom field
 * @returns The user friendly variable identifier string (that constitutes part of the variable name)
 */
function computeUserFriendlyIdentifier(
  app: ApplicationModel,
  mappedObject: MappedObject,
  property: Property,
  isCustom: boolean,
) {
  return `${makeVariableFriendly(app.name)}.${makeVariableFriendly(mappedObject.name)}.${
    isCustom ? 'custom.' : ''
  }${makeVariableFriendly(property.name)}`;
}

/**
 * Find topics belonging to an application object.
 *
 * @param topics All the topics attached to the conversation
 * @param applicationObjectUids The application object UIDs
 * @returns A filtered array of topics that belong to the app object provided.
 */
export function findApplicationObjectTopics(
  topics: ReadonlyArray<TopicModel>,
  applicationObjectUid: string,
): ReadonlyArray<TopicModel> {
  return _(topics)
    .filter((topic) => topic.objectUid === applicationObjectUid)
    .sortBy('updatedAt')
    .reverse()
    .value();
}

/**
 * Compute the custom field ID from the property ID using the application settings map.
 *
 * @param propertyId the property ID
 * @param applicationSettings the application settings
 * @returns the mapped custom field ID or undefined
 */
export function computeCustomFieldId(
  propertyId: number,
  applicationSettings: ApplicationSettingsModel,
): number | undefined {
  return applicationSettings.propertyMappings.find((mapping) => mapping.propertyId === propertyId)
    ?.customFieldId;
}

/**
 * Find what topic is being used to interpolate the app variables in the html.
 *
 * @param body clean html to parse
 * @returns the topic ID or undefined
 */
export function findTopicIdFromHtml(body: CleanHtml): string | undefined {
  // Find topic ID in the HTML
  const topicIdMatches = getRegexpMatches(topicIdAttributeRegex, body);

  if (topicIdMatches.length === 0) {
    return undefined;
  }
  const topicIdMatch = topicIdMatches[0];

  if (!topicIdMatch.groups?.topicId) {
    return undefined;
  }

  const topicId = unescape(topicIdMatch.groups?.topicId);

  return isDigit(topicId) ? topicId : undefined;
}

export function findCustomFieldIdsFromInferredTopicType(
  inferredType: InferredTopicType,
  context: ExpressionsContext,
): ReadonlyArray<number> {
  /* eslint-disable camelcase */
  const objectUid = inferredType.object_uid;

  const {dependencies} = context;
  const {applications, applicationSettings} = dependencies.applicationsState;

  const application = applications?.find(
    (app) => app.objects && app.objects.some((object) => object.uid === objectUid),
  );
  const applicationObject = application?.objects?.find(({uid}) => uid === objectUid);
  const applicationObjectPropertyFieldIds = new Set(
    applicationObject?.objectBuilder.propertyFieldAttributes.map(({propertyFieldId}) => propertyFieldId),
  );

  const appSettings = applicationSettings?.find((settings) => settings.applicationId === application?.id);

  return (
    appSettings?.propertyMappings
      .filter(({propertyId}) => applicationObject && applicationObjectPropertyFieldIds.has(propertyId))
      .map(({customFieldId}) => customFieldId) ?? []
  );
}
